import { addHours, isToday } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Variant } from '~/api/Catalog';
import { company } from '~/company/Company';
import { ModalType } from '~/components/Modal/interface';
import { formatVolume } from '~/components/ProductCard/utils';
import { useProductListAnalyticsContext } from '~/components/ProductListAnalytics/ProductListAnalyticsContext';
import { useCartVibration } from '~/hooks/useCartVibration';
import { useDateLang } from '~/hooks/useDateLang';
import { useModal } from '~/hooks/useModal';
import { useMounted } from '~/hooks/useMounted';
import { useProductLimit } from '~/hooks/useProductLimit';
import { useVibration } from '~/hooks/useVibration';
import { toAddToCartSource } from '~/services/analytics/productLists';
import { cartStore, catalogStore, mainStore, orderStore } from '~/stores';
import { type Offer, Product } from '~/stores/CategoriesStore';
import { favoritesStore } from '~/stores/FavoritesStore/FavoritesStore';
import { isProductBundle } from '~/types/Product';
import {
  calcDiscountPercentage,
  convertPenceToPounds,
  formatPriceWithCurrency,
} from '~/utils/formaters';
import {
  getApplicableMultibuyLevel,
  getLowestMultibuyLevel,
  sortMultibuyLevels,
} from '~/utils/multibuy';

import type { MultiBuyInfo, ProductCardVariant } from '../types';

/**
 * Badge type for product card
 */
export type ProductBadgeType =
  | 'discount'
  | 'express'
  | 'bundle'
  | 'multibuy'
  | 'comingSoon'
  | 'deliveryDate'
  | 'deliveryToday'
  | 'suggested'
  | 'catalog';

/**
 * Badge data structure
 */
export interface ProductBadge {
  type: ProductBadgeType;
  content: string | number;
  deliveryDate?: Date | null;
  deliveryTodayMobile?: boolean;
  onClick?: () => void;
  /** Reference-book id, present on catalog badges only. Used as the React key. */
  id?: number;
  /** Catalog-supplied fill; falls back to the design system when unusable. */
  backgroundColor?: Nullable<string>;
  /** Catalog-supplied text color; falls back to the design system. */
  textColor?: Nullable<string>;
}

/**
 * How many badges fit on a product card.
 */
export const CARD_BADGES_LIMIT = 3;

/**
 * Merges catalog badges with the system ones for a product card.
 *
 * Catalog badges come first in the order set in the catalog; system badges
 * follow, keeping their existing relative order. Every system badge is
 * guaranteed a slot — catalog badges shrink to make room. Without that
 * reservation a merchandiser assigning badges would silently hide the
 * auto-calculated discount on every product in the set.
 *
 * Note this intentionally diverges from BRD JS-14286 §2.4, which drops the
 * discount when it lands fourth.
 */
export const composeCardBadges = (
  catalogBadges: ProductBadge[],
  systemBadges: ProductBadge[],
): ProductBadge[] => {
  const catalogSlots = Math.max(0, CARD_BADGES_LIMIT - systemBadges.length);

  return [...catalogBadges.slice(0, catalogSlots), ...systemBadges].slice(
    0,
    CARD_BADGES_LIMIT,
  );
};

/**
 * Return type for useProductCardV3 hook
 */
export interface UseProductCardV3Return {
  currentItem: Offer | Product;
  volumes: Array<{ id: string; value: string }>;
  limit: number;
  image: string;
  leadTime: number;
  oldPrice: string | null;
  currentPrice: string;
  totalCartPrice: string | null;
  cartItemsCount: number;
  isMultiBuy: boolean;
  isMultiBuyActive: boolean;
  isExpress: boolean;
  isBackorderAvailable: boolean;
  isBundle: boolean;
  isFavorite: boolean;
  isOutOfStock: boolean;
  productHasVariants: number;
  productHasManyVariants: boolean;
  colorVariants: Variant[];
  /** Catalog + system badges, already capped at {@link CARD_BADGES_LIMIT}. */
  badges: ProductBadge[];
  multiBuyInfo: MultiBuyInfo | null;
  selectOffer: (sku: string) => void;
  toggleFavorite: () => void;
  changeCount: (count: number, action: 'add' | 'remove') => void;
  addMultiBuyProductToCart: () => void;
  openSelectSKUModal: () => void;
}

/**
 * Business logic hook for ProductCardV3
 * Adapted from existing useProductCard hook
 */
export const useProductCardV3 = (
  product: Product,
  currentOffer?: Offer | null,
  size?: ProductCardVariant,
  hideBadges?: boolean,
): UseProductCardV3Return => {
  const { t } = useTranslation();
  const locale = useDateLang();
  const mounted = useMounted();
  const productList = useProductListAnalyticsContext();
  const [offer, setOffer] = useState(currentOffer ?? product.defaultOffer);

  useEffect(() => {
    if (currentOffer) {
      setOffer(currentOffer);
    }
  }, [currentOffer]);

  const isFavorite = mounted && favoritesStore.isFavorited(product.id);

  const isBundle = isProductBundle(product);
  const { openModal } = useModal();

  const currentItemWithoutProperties = isBundle ? product : offer || product;
  const propertyFromVariant = product.variants?.find(
    ({ sku }) => sku === currentItemWithoutProperties.sku,
  );
  const propertiesFromVariant = {};
  if (propertyFromVariant) {
    propertiesFromVariant[`${propertyFromVariant.property}`] = {
      ...currentItemWithoutProperties.properties[propertyFromVariant.property],
      value: propertyFromVariant.value,
    };
  }
  if (Object.keys(propertiesFromVariant).length > 0) {
    // temporarily substitute properties if needed, but keep class instance
    // Use Object.defineProperty to override properties field if needed, non-intrusively
    Object.defineProperty(currentItemWithoutProperties, 'properties', {
      value: {
        ...currentItemWithoutProperties.properties,
        ...propertiesFromVariant,
      },
      writable: true,
      configurable: true,
      enumerable: true,
    });
  }
  const currentItem = currentItemWithoutProperties;

  const [image, setImage] = useState(currentItem.previewImageThumb);
  const { limit, currentWhSellable, parentWhSellable } = useProductLimit({
    offer: currentItem,
  });

  const cartItemsCount = mounted
    ? cartStore.getCartItemCountById(currentItem)
    : 0;

  const lowestMultibuyLevel = currentItem.multibuyPriceLevels?.length
    ? getLowestMultibuyLevel(currentItem.multibuyPriceLevels)
    : null;
  const isMultiBuy = !isBundle && Boolean(lowestMultibuyLevel);

  const applicableLevel = currentItem.multibuyPriceLevels?.length
    ? getApplicableMultibuyLevel(
        currentItem.multibuyPriceLevels,
        cartItemsCount,
      )
    : null;

  const { vibrateOnChange } = useCartVibration({
    isMultibuy: isMultiBuy,
    requiredQuantity: lowestMultibuyLevel?.quantity ?? 0,
    currentCount: cartItemsCount,
  });

  const isMultiBuyActive = Boolean(isMultiBuy && applicableLevel !== null);

  const hasDiscount = currentItem.discountAmount > 0;

  const currentPrice =
    isMultiBuyActive && applicableLevel
      ? convertPenceToPounds(applicableLevel.unitPrice)
      : hasDiscount
        ? currentItem.discountPriceFormatted
        : currentItem.priceFormatted;

  const oldPrice = hasDiscount ? currentItem.priceFormatted : null;

  const isExpress =
    mounted &&
    company.showExpressBadge &&
    currentItem &&
    currentWhSellable &&
    currentWhSellable >= cartItemsCount &&
    orderStore.isExpressAvailableNow;
  const isBackorderAvailable =
    currentItem &&
    currentItem.isBackorderAvailable &&
    !!currentItem.backorderLeadTime &&
    parentWhSellable > 0;

  const leadTime = currentItem.backorderLeadTime
    ? currentItem.backorderLeadTime
    : 0;

  const productHasVariants = useMemo(() => {
    return product.variants?.length
      ? product.variants.map(({ sku, value }) => ({ id: sku, value }))
      : product.offers.map((item) => ({
          id: item.sku,
          value: formatVolume(item.properties),
        }));
  }, [product.variants, product.offers]).length;

  const productHasManyVariants = productHasVariants > 1;

  const colorVariants = useMemo(
    () =>
      Object.values(
        product.variants?.reduce((acc: Record<string, Variant>, item) => {
          if (item.property === 'color' && !acc[item.value]) {
            acc[item.value] = item;
          }
          return acc;
        }, {}) || {},
      ).sort((a, b) => a.sorting - b.sorting),
    [product.variants],
  );

  const volumes = useMemo(() => {
    return product.variants?.length
      ? product.variants
          .filter(({ property }) => property === 'volume')
          .map(({ sku, value }) => ({ id: sku, value }))
      : product.offers.map((item) => ({
          id: item.sku,
          value: formatVolume(item.properties),
        }));
  }, [product.variants, product.offers]);

  const selectOffer = useCallback(
    (sku: string) => {
      const newOffer = product.offers.find((item) => item.sku === sku);

      if (newOffer) {
        setImage(newOffer.previewImageThumb);
        return setOffer(newOffer);
      }

      const variant = product.variants?.find((variant) => variant.sku === sku);

      if (!variant) {
        return;
      }

      const { thumb, url } = (variant.images || []).reduce<{
        thumb: string;
        url: string;
      }>(
        (acc, { thumb, url }) => {
          if (thumb && !acc.thumb) {
            acc.thumb = thumb;
          }

          if (url && !acc.url) {
            acc.url = url;
          }

          return acc;
        },
        { thumb: '', url: '' },
      );

      if (thumb || url) {
        setImage(thumb || url);
      }

      Product.loadOffer(product, sku).then((offersMap) => {
        const newOffer = offersMap.get(sku);
        if (newOffer) {
          setImage(newOffer.previewImageThumb);
          setOffer(newOffer);
        }
      });
    },
    [product],
  );

  const { vibrate: vibrateFavorite } = useVibration({ type: 'soft' });

  const toggleFavorite = useCallback(() => {
    favoritesStore.toggleFavorite(product, 'product_main');
    vibrateFavorite();
  }, [product, vibrateFavorite]);

  const changeCount = useCallback(
    (count: number, action: 'add' | 'remove') => {
      vibrateOnChange(count, action);

      cartStore.setCartItemCountByProduct(
        {
          ...currentItem,
          variants: product.variants,
          ratingAverage: currentItem?.ratingAverage ?? 0,
          ratingMarksCount: currentItem?.ratingMarksCount ?? 0,
        },
        count,
        action,
        toAddToCartSource(productList),
      );
    },
    [currentItem, product, productList, vibrateOnChange],
  );

  const addMultiBuyProductToCart = useCallback(() => {
    if (!lowestMultibuyLevel) {
      return;
    }

    const targetCount = lowestMultibuyLevel.quantity - cartItemsCount;

    if (targetCount <= 0 || limit < cartItemsCount + targetCount) {
      return;
    }

    changeCount(cartItemsCount + targetCount, 'add');
  }, [lowestMultibuyLevel, changeCount, cartItemsCount, limit]);

  const openSelectSKUModal = useCallback(() => {
    catalogStore.setSelectedCatalogProduct(
      product.slug,
      toAddToCartSource(productList),
    );
    openModal(ModalType.SelectSKU);
  }, [product, productList]);

  useEffect(() => {
    if (product) {
      setOffer(product.defaultOffer);
      setImage(currentItem.previewImageThumb);
    }
  }, [product]);

  // Raw (unformatted) multibuy amounts, kept separate from currency
  // formatting so that `formatPriceWithCurrency` (which reads global
  // context via a hook) is never invoked from inside `useMemo`.
  const multiBuyInfoRaw = useMemo(() => {
    if (
      !isMultiBuy ||
      !lowestMultibuyLevel ||
      !currentItem.multibuyPriceLevels?.length
    ) {
      return null;
    }

    const sortedLevels = sortMultibuyLevels(currentItem.multibuyPriceLevels);
    const isActive = applicableLevel !== null;

    // Always find next unreached level (even if multibuy is active)
    // This allows showing remainingCount to the next level after reaching current one
    const nextLevel = sortedLevels.find(
      (level: (typeof sortedLevels)[0]) => cartItemsCount < level.quantity,
    );

    // Use applicable level if active, otherwise use next level or lowest level
    const levelToUse = applicableLevel || nextLevel || lowestMultibuyLevel;
    const promoPrice = levelToUse.unitPrice;
    const promoQuantity = levelToUse.quantity;

    if (!promoPrice || promoPrice <= 0) {
      return null;
    }

    // Calculate remaining count from next unreached level, or 0 if all levels are reached
    const remainingCount = nextLevel
      ? Math.max(0, nextLevel.quantity - cartItemsCount)
      : 0;

    const basePrice = currentItem.price;

    if (basePrice <= 0 || promoPrice >= basePrice) {
      return null;
    }

    const discountPercent = calcDiscountPercentage(basePrice, promoPrice);

    // Calculate discount percent for next level if it exists
    const nextDiscountPercent = nextLevel
      ? (() => {
          const nextPromoPrice = nextLevel.unitPrice;
          if (
            nextPromoPrice <= 0 ||
            basePrice <= 0 ||
            nextPromoPrice >= basePrice
          ) {
            return null;
          }
          return calcDiscountPercentage(basePrice, nextPromoPrice);
        })()
      : null;

    return {
      remainingCount,
      discountPercent,
      discountAmount: mainStore.convertPenceToPounds(
        (basePrice - promoPrice) * promoQuantity,
      ),
      nextDiscountPercent,
      nextDiscountAmount: nextLevel
        ? mainStore.convertPenceToPounds(
            (basePrice - nextLevel.unitPrice) * nextLevel.quantity,
          )
        : null,
      isActive,
    };
  }, [
    isMultiBuy,
    lowestMultibuyLevel,
    applicableLevel,
    currentItem.multibuyPriceLevels,
    currentItem.discountPrice,
    currentItem.price,
    cartItemsCount,
  ]);

  const multiBuyInfo: MultiBuyInfo | null = multiBuyInfoRaw
    ? {
        remainingCount: multiBuyInfoRaw.remainingCount,
        discountPercent: multiBuyInfoRaw.discountPercent,
        discountValue: formatPriceWithCurrency(multiBuyInfoRaw.discountAmount),
        nextDiscountPercent: multiBuyInfoRaw.nextDiscountPercent,
        nextDiscountValue: multiBuyInfoRaw.nextDiscountAmount
          ? formatPriceWithCurrency(multiBuyInfoRaw.nextDiscountAmount)
          : null,
        isActive: multiBuyInfoRaw.isActive,
      }
    : null;

  const systemBadges = useMemo<ProductBadge[]>(() => {
    const badgesList: ProductBadge[] = [];

    if (hideBadges) {
      return [];
    }

    if (limit <= 0) {
      return badgesList;
    }

    if (!product.isBought && size === 's' && !company.isSaas) {
      badgesList.push({
        type: 'suggested',
        content: t('suggested'),
      });
    }

    if (currentItem.discountAmount > 0 && currentItem.price) {
      const discountPercent = calcDiscountPercentage(
        currentItem.price,
        currentItem.discountPrice,
      );
      badgesList.push({
        type: 'discount',
        content: `-${discountPercent} %`,
      });
    }

    if (isBundle) {
      badgesList.push({
        type: 'bundle',
        content: t('bundle'),
      });
    }

    if (isBackorderAvailable) {
      const isDeliveryToday = isToday(addHours(new Date(), leadTime));
      const backorderDate = isDeliveryToday
        ? null
        : addHours(new Date(), leadTime);

      badgesList.push({
        type: isDeliveryToday ? 'deliveryToday' : 'deliveryDate',
        content: isDeliveryToday
          ? t('deliveryTodayMobile')
          : t('slotDeliveryInShort', { backorderDate }),
        deliveryDate: backorderDate,
      });
    }

    if (isExpress) {
      badgesList.push({
        type: 'express',
        content: t('express'),
      });
    }

    return company.showOnlyDiscountBadgeOnProductCards
      ? badgesList.filter(({ type }) => type === 'discount')
      : badgesList;
  }, [
    limit,
    product.isBought,
    isBackorderAvailable,
    leadTime,
    currentItem.discountAmount,
    currentItem.discountPrice,
    currentItem.price,
    isExpress,
    isBundle,
    locale,
    t,
  ]);

  const catalogBadges = useMemo<ProductBadge[]>(() => {
    // Bundles inherit their offers' badges through the shared parsing path,
    // but the CMS cannot assign badges to a bundle itself — showing the
    // borrowed ones was agreed off until it can.
    // Unlike system badges, there is no stock gate: catalog badges are
    // merchandising data and must survive out-of-stock surfaces (waitlist).
    if (hideBadges || isBundle) {
      return [];
    }

    // Guarded: cart items rehydrated from storage predate this field, and
    // several call sites hand the hook a plain spread copy rather than an
    // Offer instance.
    return (currentItem.catalogBadges ?? []).map((badge) => ({
      type: 'catalog' as const,
      id: badge.id,
      content: badge.name,
      backgroundColor: badge.backgroundColor,
      textColor: badge.textColor,
    }));
  }, [hideBadges, isBundle, currentItem.catalogBadges]);

  const badges = useMemo<ProductBadge[]>(
    () => composeCardBadges(catalogBadges, systemBadges),
    [catalogBadges, systemBadges],
  );

  // Calculate total price in cart (current price × quantity)
  const totalCartPrice = useMemo(() => {
    if (isMultiBuy && isMultiBuyActive && applicableLevel) {
      const promoPrice = mainStore.convertPenceToPounds(
        applicableLevel.unitPrice * cartItemsCount,
      );

      return promoPrice;
    }

    const priceToUse =
      currentItem?.discountAmount && currentItem.discountAmount > 0
        ? currentItem.discountPrice
        : currentItem?.price || 0;

    return mainStore.convertPenceToPounds(priceToUse * cartItemsCount);
  }, [
    cartItemsCount,
    isMultiBuy,
    isMultiBuyActive,
    applicableLevel,
    currentItem,
  ]);

  return {
    currentItem,
    volumes,
    limit,
    image,
    leadTime,
    oldPrice: oldPrice ? formatPriceWithCurrency(oldPrice) : null,
    currentPrice: formatPriceWithCurrency(currentPrice),
    totalCartPrice: totalCartPrice
      ? formatPriceWithCurrency(totalCartPrice)
      : null,
    cartItemsCount,
    isMultiBuy,
    isMultiBuyActive,
    isExpress: Boolean(isExpress),
    isBackorderAvailable,
    isBundle,
    isFavorite,
    isOutOfStock: limit <= 0,
    productHasVariants,
    productHasManyVariants,
    colorVariants: [...new Set(colorVariants)],
    badges,
    multiBuyInfo,
    selectOffer,
    toggleFavorite,
    changeCount,
    addMultiBuyProductToCart,
    openSelectSKUModal,
  };
};
